// The grouped sidebar menu: one collapsible heading per group (with its page
// count), the pages as links. Which groups are open is decided by GroupedSider.
import React from 'react';
import { Menu, type MenuProps } from 'antd';
import {
  AppstoreOutlined,
  BarChartOutlined,
  BellOutlined,
  ExperimentOutlined,
  FireOutlined,
  SettingOutlined,
  TeamOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import type { SiderGroup } from './sidebarGroups';

const GROUP_ICON: Record<string, React.ReactNode> = {
  dashboards: <BarChartOutlined />,
  players: <TeamOutlined />,
  liveops: <FireOutlined />,
  pushes: <BellOutlined />,
  experiments: <ExperimentOutlined />,
  pvp: <ThunderboltOutlined />,
  system: <SettingOutlined />,
};

export interface SiderMenuProps {
  groups: SiderGroup[];
  openKeys: string[];
  onOpenChange: (keys: string[]) => void;
  selectedKey?: string;
  collapsed: boolean;
  /** Extra items under the groups (logout). */
  footer?: MenuProps['items'];
  onClick?: () => void;
  style?: React.CSSProperties;
}

export const SiderMenu: React.FC<SiderMenuProps> = ({
  groups,
  openKeys,
  onOpenChange,
  selectedKey,
  collapsed,
  footer = [],
  onClick,
  style,
}) => {
  const items: MenuProps['items'] = [
    ...groups.map((g) => ({
      key: g.key,
      icon: GROUP_ICON[g.key] ?? <AppstoreOutlined />,
      label: (
        <span data-sider-group={g.key}>
          <span>{g.label}</span>{' '}
          <span data-sider-count style={{ opacity: 0.45, fontSize: 12 }}>
            {g.leaves.length}
          </span>
        </span>
      ),
      children: g.leaves.map((l) => ({
        key: l.key,
        label: <Link to={l.route}>{l.label}</Link>,
      })),
    })),
    ...footer,
  ];
  return (
    <Menu
      mode="inline"
      items={items}
      selectedKeys={selectedKey ? [selectedKey] : []}
      // A collapsed sider shows groups as fly-outs; antd manages those itself.
      {...(collapsed ? {} : { openKeys, onOpenChange })}
      onClick={onClick}
      style={{ border: 'none', ...style }}
    />
  );
};
